import React, { useEffect, useState, SVGProps } from "react";
import { Helmet } from "react-helmet-async";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import { PublicPageLayoutInner } from "../../Layout/PublicPageLayout";
import "./HomePageV2.css";

const AppleLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 814 1000" fill="currentColor" {...props}>
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.8 0 663.8 0 542.3 0 341.8 133.4 235.9 264.4 235.9c70.1 0 128.4 46.4 172.3 46.4 41.8 0 107.3-49.1 185.2-49.1 29.4 0 108.2 2.6 162.3 90.1zm-162.1-171.4c-37.4 44.5-97.7 79.3-162 79.3-5.5 0-11.5-.6-17-.6-2.6-57.8 21.4-117.2 57.8-157.7C543.1 51.7 614 26.7 678.9 25.2c3.8 57.2-14.6 113.8-52.9 144.3z" />
  </svg>
);

const GooglePlayLogo = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 512 512" {...props}>
    <path fill="#4CAF50" d="M47 0C34 6.8 25.3 19.2 25.3 35V477c0 15.8 8.7 28.2 21.7 35L291.2 269 47 0z"/>
    <path fill="#FF5252" d="M386.8 199.3L325.3 234.3 104.6 13 386.8 199.3z"/>
    <path fill="#FFC107" d="M464.7 232.3c-18.3 9.5-18 27.5 0 37.8L386.8 312.7 325.3 277.7l60.1-60.1 79.3 14.7z" />
    <path fill="#2196F3" d="M325.3 277.7L104.6 499l280.8-186.3-60.1-35z"/>
  </svg>
);

const CHAT_MSGS = [
  { from: "ai", text: "Hola! Vamos a practicar. Como fue tu dia hoy?" },
  { from: "user", text: "Fue muy bien, gracias!" },
  { from: "ai", text: 'Perfecto! Small correction: use "fue muy bien" with an accent. Try saying it again.' },
  { from: "user", text: "Fue muy bien!" },
  { from: "ai", text: "Exactly! Your Spanish is really improving." },
];

const ChatPhone: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="v2-phone-outer">
      <div className="v2-phone-glow-ring" />
      <div className="v2-chip v2-chip-1">{t("v2ChipSpanish")}</div>
      <div className="v2-chip v2-chip-2">{t("v2ChipAiTyping")}</div>
      <div className="v2-chip v2-chip-3">{t("v2ChipStreak")}</div>
      <div className="v2-phone-frame">
        <div className="v2-status-bar">
          <span>9:41</span>
          <span className="v2-status-icons">●●●</span>
        </div>
        <div className="v2-chat-hdr">
          <div className="v2-tutor-avatar"><span>S</span></div>
          <div className="v2-tutor-info">
            <div className="v2-tutor-name">Sofia</div>
            <div className="v2-tutor-sub">{t("v2TutorSub")}</div>
          </div>
          <div className="v2-online-dot" />
        </div>
        <div className="v2-chat-body">
          <div className="v2-day-label">{t("v2ChatToday")}</div>
          {CHAT_MSGS.map((m, i) => (
            <div key={i} className={`v2-msg-row ${m.from === "user" ? "v2-row-user" : "v2-row-ai"}`}>
              {m.from === "ai" && <div className="v2-msg-avatar"><span>S</span></div>}
              <div className={`v2-msg-bubble ${m.from === "user" ? "v2-bubble-user" : "v2-bubble-ai"}`}>
                {m.text}
              </div>
            </div>
          ))}
          <div className="v2-msg-row v2-row-ai">
            <div className="v2-msg-avatar"><span>S</span></div>
            <div className="v2-bubble-ai v2-typing"><span /><span /><span /></div>
          </div>
        </div>
        <div className="v2-chat-input">
          <div className="v2-input-field">{t("v2ChatInputPlaceholder")}</div>
          <button className="v2-send-btn" type="button">↑</button>
        </div>
      </div>
    </div>
  );
};

const PlacementTestPopup: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="v2-popup-wrap">
      <div className="v2-popup">
        <button className="v2-popup-close" type="button" onClick={() => setVisible(false)}>✕</button>
        <div className="v2-popup-icon">🎯</div>
        <h3 className="v2-popup-title">{t("v2PopupTitle")}</h3>
        <p className="v2-popup-desc">{t("v2PopupDesc")}</p>
        <button
          className="v2-popup-btn"
          type="button"
          onClick={() => (window.location.href = "/placement-test?l=en-us")}
        >
          {t("v2PopupBtn")}
        </button>
        <p className="v2-popup-footnote">{t("v2PopupFootnote")}</p>
      </div>
    </div>
  );
};

const HomeV2Content: React.FC = () => {
  const { t } = useLanguage();
  const [showBazaar, setShowBazaar] = useState(false);

  useEffect(() => {
    fetch("/api/settings")
      .then((r) => r.json())
      .then((d) => {
        if (d.data) {
          const s = d.data.find((x: { key: string }) => x.key === "show-bazaar-download-link");
          if (s) setShowBazaar(s.value === "true" || s.value === true);
        }
      })
      .catch(() => {});
  }, []);

  const openIOS = () => window.open("https://apps.apple.com/us/app/lingua-talk/id6746571204");
  const openAndroid = () => window.open("https://play.google.com/store/apps/details?id=com.parsa.lingua_talk");

  const heroFlags = [
    { code: "gb", name: t("v2MarqueeEnglish") },
    { code: "es", name: t("v2MarqueeSpanish") },
    { code: "fr", name: t("v2MarqueeFrench") },
    { code: "de", name: t("v2MarqueeGerman") },
    { code: "it", name: t("v2MarqueeItalian") },
    { code: "tr", name: t("v2MarqueeTurkish") },
  ];

  const marqueeLangs = [
    { flag: "🇺🇸", name: t("v2MarqueeEnglish") },
    { flag: "🇪🇸", name: t("v2MarqueeSpanish") },
    { flag: "🇫🇷", name: t("v2MarqueeFrench") },
    { flag: "🇩🇪", name: t("v2MarqueeGerman") },
    { flag: "🇮🇹", name: t("v2MarqueeItalian") },
    { flag: "🇹🇷", name: t("v2MarqueeTurkish") },
  ];

  const features = [
    { icon: "🤖", title: t("v2Feature1Title"), desc: t("v2Feature1Desc") },
    { icon: "💬", title: t("v2Feature2Title"), desc: t("v2Feature2Desc") },
    { icon: "🎯", title: t("v2Feature3Title"), desc: t("v2Feature3Desc") },
    { icon: "🌍", title: t("v2Feature4Title"), desc: t("v2Feature4Desc") },
  ];

  const steps = [
    { n: "01", title: t("v2Step1Title"), desc: t("v2Step1Desc") },
    { n: "02", title: t("v2Step2Title"), desc: t("v2Step2Desc") },
    { n: "03", title: t("v2Step3Title"), desc: t("v2Step3Desc") },
  ];

  const miniStats = [
    ["142", t("v2StatLessons")],
    ["18", t("v2StatStreak")],
    ["2.4k", t("v2StatWords")],
  ];

  return (
    <>
      <Helmet>
        <title>{t("v2MetaTitle")}</title>
        <meta name="description" content={t("v2MetaDescription")} />
        <link rel="canonical" href="https://linguatalk.app" />
      </Helmet>

      <PlacementTestPopup />
      <PublicPageLayoutInner>
        <section className="v2-hero">
          <div className="v2-hero-text">
            <h1 className="v2-headline">
              {t("v2HeroLine1")}<br />
              <span className="v2-grad-text">{t("v2HeroLine2")}</span>
            </h1>
            <div className="v2-hero-flags" aria-label="Supported languages">
              {heroFlags.map(({ code, name }) => (
                <img
                  key={code}
                  className="v2-hero-flag"
                  src={`https://flagcdn.com/w80/${code}.png`}
                  srcSet={`https://flagcdn.com/w160/${code}.png 2x`}
                  width={36}
                  height={36}
                  alt={name}
                  title={name}
                  loading="lazy"
                />
              ))}
            </div>
            <div className="v2-pill">{t("v2Pill")}</div>
            <p className="v2-subtext">{t("v2Subtext")}</p>
            <div className="v2-btns">
              <button className="v2-store-btn v2-store-apple" type="button" onClick={openIOS}>
                <AppleLogo className="v2-store-logo" />
                <span className="v2-store-label">
                  <span className="v2-store-sub">{t("v2StoreSubApple")}</span>
                  <span className="v2-store-name">{t("v2StoreNameApple")}</span>
                </span>
              </button>
              <button className="v2-store-btn v2-store-android" type="button" onClick={openAndroid}>
                <GooglePlayLogo className="v2-store-logo" />
                <span className="v2-store-label">
                  <span className="v2-store-sub">{t("v2StoreSubGoogle")}</span>
                  <span className="v2-store-name">{t("v2StoreNameGoogle")}</span>
                </span>
              </button>
              {showBazaar && (
                <button
                  className="v2-store-btn v2-store-android"
                  type="button"
                  onClick={() => window.open("https://cafebazaar.ir/app/com.parsa.lingua_talk")}
                >
                  <span className="v2-store-label">
                    <span className="v2-store-sub">{t("v2BazaarSub")}</span>
                    <span className="v2-store-name">{t("v2BazaarName")}</span>
                  </span>
                </button>
              )}
            </div>
          </div>
          <div className="v2-hero-visual">
            <ChatPhone />
          </div>
        </section>

        <div className="v2-marquee-wrap">
          <div className="v2-marquee">
            {[...marqueeLangs, ...marqueeLangs].map((lang, i) => (
              <span key={i} className="v2-marquee-item">
                <span className="v2-marquee-flag">{lang.flag}</span>
                {lang.name}
                <span className="v2-dot"> · </span>
              </span>
            ))}
          </div>
        </div>

        <section className="v2-what">
          <div className="v2-what-inner">
            <div className="v2-what-tag">{t("v2WhatTag")}</div>
            <h2 className="v2-what-title">
              {t("v2WhatTitle1")}<br />
              <span className="v2-grad-text">{t("v2WhatTitle2")}</span>
            </h2>
            <p className="v2-what-desc">{t("v2WhatDesc")}</p>
          </div>
        </section>

        <section className="v2-features">
          <div className="v2-section-label">{t("v2WhyLabel")}</div>
          <div className="v2-cards">
            {features.map(({ icon, title, desc }) => (
              <div key={title} className="v2-card">
                <div className="v2-card-icon">{icon}</div>
                <h3 className="v2-card-title">{title}</h3>
                <p className="v2-card-desc">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="v2-how">
          <div className="v2-how-text">
            <div className="v2-section-label">{t("v2HowLabel")}</div>
            <h2 className="v2-how-title">
              {t("v2HowTitle1")}<br />
              <span className="v2-grad-text">{t("v2HowTitle2")}</span>
            </h2>
            <div className="v2-steps">
              {steps.map(({ n, title, desc }) => (
                <div key={n} className="v2-step">
                  <div className="v2-step-n">{n}</div>
                  <div>
                    <div className="v2-step-title">{title}</div>
                    <div className="v2-step-desc">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="v2-how-visual">
            <div className="v2-how-card">
              <div className="v2-how-glow" />
              <svg viewBox="0 0 120 120" className="v2-ring-svg" width="130" height="130">
                <defs>
                  <linearGradient id="rg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#d946ef" />
                    <stop offset="100%" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="52" className="v2-ring-bg" />
                <circle cx="60" cy="60" r="52" className="v2-ring-fg" stroke="url(#rg)" />
              </svg>
              <div className="v2-ring-label">
                <span className="v2-ring-num">B2</span>
                <span className="v2-ring-sub">{t("v2RingSub")}</span>
              </div>
              <div className="v2-mini-stats">
                {miniStats.map(([n, l]) => (
                  <div key={l} className="v2-mini-stat"><span>{n}</span><small>{l}</small></div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="v2-cta">
          <div className="v2-cta-glow" aria-hidden="true" />
          <h2 className="v2-cta-title">{t("v2CtaTitle")}</h2>
          <p className="v2-cta-sub">{t("v2CtaSub")}</p>
          <div className="v2-btns v2-btns-center">
            <button className="v2-store-btn v2-store-apple" type="button" onClick={openIOS}>
              <AppleLogo className="v2-store-logo" />
              <span className="v2-store-label">
                <span className="v2-store-sub">{t("v2StoreSubApple")}</span>
                <span className="v2-store-name">{t("v2StoreNameApple")}</span>
              </span>
            </button>
            <button className="v2-store-btn v2-store-android" type="button" onClick={openAndroid}>
              <GooglePlayLogo className="v2-store-logo" />
              <span className="v2-store-label">
                <span className="v2-store-sub">{t("v2StoreSubGoogle")}</span>
                <span className="v2-store-name">{t("v2StoreNameGoogle")}</span>
              </span>
            </button>
          </div>
        </section>
      </PublicPageLayoutInner>
    </>
  );
};

const HomePageV2: React.FC = () => (
  <LanguageProvider>
    <HomeV2Content />
  </LanguageProvider>
);

export default HomePageV2;
